import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Typography } from "@material-tailwind/react";
import toast from "react-hot-toast";

import AlertMessage from "../../common/actions/AlertMessage";
import Loader from "../../common/loader/Loader";
import NoContent from "../../common/shared/NoContent";
import SectionTitle from "../../common/shared/SectionTitle";
import { getUserSurveys } from "../../redux/actions/survey_action";

const links = [{ name: "Surveys & Polls" }];

function Surveys() {
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { surveys, surveyError } = useSelector((state) => state.survey);

  useEffect(() => {
    dispatch(getUserSurveys()).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (!location.state?.surveySuccess) {
      return;
    }

    toast.success(location.state.surveySuccess);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const getSurveyStatus = (survey) => {
    const now = new Date();
    const startDate = survey.starts_at ? new Date(survey.starts_at) : null;
    const endDate = survey.ends_at ? new Date(survey.ends_at) : null;

    if (survey.hasResponded) {
      return {
        label: "completed",
        className: "bg-green-50 text-green-700",
      };
    }

    if (survey.status === "archived" || (endDate && endDate < now)) {
      return {
        label: "cancel",
        className: "bg-red-50 text-red-700",
      };
    }

    if (startDate && startDate > now) {
      return {
        label: "pending",
        className: "bg-blue-50 text-blue-700",
      };
    }

    return {
      label: "in-progress",
      className: "bg-amber-50 text-amber-700",
    };
  };

  return (
    <>
      <SectionTitle
        title="Surveys & Polls"
        description="Participate in active member polls and executive surveys."
        children={links}
        link="/home"
      />

      <div className="my-8 max-w-[1200px] m-auto px-4">
        {surveyError && <AlertMessage type="error" message={surveyError} />}

        {loading ? (
          <Loader />
        ) : surveys.length === 0 ? (
          <NoContent message="There are no active surveys at the moment." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {surveys.map((survey) => {
              const statusBadge = getSurveyStatus(survey);

              return (
                <Card key={survey.id} className="border border-blue-gray-50">
                  <CardBody>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <Typography variant="h5" color="blue-gray">
                          {survey.title}
                        </Typography>
                        <Typography className="mt-2 text-sm text-blue-gray-700">
                          {survey.description || "No description provided."}
                        </Typography>
                      </div>

                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusBadge.className}`}
                      >
                        {statusBadge.label}
                      </span>
                    </div>

                    <div className="space-y-1 text-sm text-black">
                      <Typography variant="small">
                        <span className="font-bold"> Starts:</span>{" "}
                        {survey.starts_at
                          ? new Date(survey.starts_at).toDateString()
                          : "Immediate"}
                      </Typography>
                      <Typography variant="small">
                        <span className="font-bold"> Ends:</span>{" "}
                        {survey.ends_at
                          ? new Date(survey.ends_at).toDateString()
                          : "Open ended"}
                      </Typography>
                      <Typography variant="small">
                        <span className="font-bold"> Questions:</span>{" "}
                        {survey.questions?.length || 0}
                      </Typography>
                    </div>

                    <Button
                      className="mt-6"
                      fullWidth
                      onClick={() => navigate(`/home/surveys/${survey.id}`)}
                    >
                      {survey.hasResponded ? "View Submission" : "Open Survey"}
                    </Button>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default Surveys;
